import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import type { ReportSubAccountDisplayMetadata } from '../snapshot/buildReportDisplayMetadata';

export type CashModeInsightKind = 'raise' | 'deploy';

export type CashModeInsightSource = {
  accountLabel: string;
  holdingLabels: string[];
  subAccountId: string;
};

type SubAccountResult = NonNullable<
  OptimizerAuditV2Record['subAccountResults']
>[number];

const holdingLabelFromDiffRows = (
  financialProductId: string,
  diffRows: WeightDiffDetailRow[],
): string | undefined =>
  diffRows.find((row) => row.financialProductId === financialProductId)?.label;

const fallbackHoldingLabel = (financialProductId: string): string =>
  financialProductId.slice(-8);

const resolveAccountLabel = (
  subAccount: SubAccountResult,
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>,
): string => {
  const fromReport = subAccount.subAccountId
    ? subAccountsById.get(subAccount.subAccountId)
    : undefined;

  if (fromReport?.name) {
    return fromReport.name;
  }

  return formatAuditAccountType(subAccount.accountType);
};

const sleeveTradeCents = (
  subAccount: SubAccountResult,
  kind: CashModeInsightKind,
): number =>
  (subAccount.weightDiffs ?? []).reduce((sum, diff) => {
    const deltaCents = diff.deltaCents ?? 0;
    if (kind === 'raise') {
      return deltaCents < 0 ? sum + Math.abs(deltaCents) : sum;
    }

    return deltaCents > 0 ? sum + deltaCents : sum;
  }, 0);

const sleeveMatchesAmount = (
  subAccount: SubAccountResult,
  kind: CashModeInsightKind,
  cents: number,
): boolean => {
  const reportedTotal =
    kind === 'raise'
      ? (subAccount.totalSellCents ?? 0)
      : (subAccount.totalBuyCents ?? 0);

  if (reportedTotal === cents) {
    return true;
  }

  return sleeveTradeCents(subAccount, kind) === cents;
};

const holdingLabelsForSleeve = (
  subAccount: SubAccountResult,
  kind: CashModeInsightKind,
  cents: number,
  count: number,
  diffRows: WeightDiffDetailRow[],
): string[] => {
  const diffs = (subAccount.weightDiffs ?? []).filter((diff) => {
    const deltaCents = diff.deltaCents ?? 0;
    return kind === 'raise' ? deltaCents < 0 : deltaCents > 0;
  });

  const matched =
    count === 1
      ? diffs.filter((diff) => Math.abs(diff.deltaCents ?? 0) === cents)
      : diffs;
  const source = matched.length > 0 ? matched : diffs;

  return source
    .map((diff) => {
      const financialProductId = diff.financialProductId ?? '';
      if (!financialProductId) {
        return null;
      }

      return (
        holdingLabelFromDiffRows(financialProductId, diffRows) ??
        fallbackHoldingLabel(financialProductId)
      );
    })
    .filter((label): label is string => !!label);
};

export const resolveCashModeInsightSource = ({
  kind,
  cents,
  count,
  audit,
  diffRows = [],
  subAccountsById,
  usedSubAccountIds,
}: {
  kind: CashModeInsightKind;
  cents: number;
  count: number;
  audit?: OptimizerAuditV2Record;
  diffRows?: WeightDiffDetailRow[];
  subAccountsById: Map<string, ReportSubAccountDisplayMetadata>;
  usedSubAccountIds?: Set<string>;
}): CashModeInsightSource | null => {
  if (!audit || cents <= 0) {
    return null;
  }

  const candidates = (audit.subAccountResults ?? [])
    .map((subAccount, index) => ({
      subAccount,
      subAccountId: subAccount.subAccountId || `__index_${index}`,
    }))
    .filter(({ subAccount, subAccountId }) => {
      if (usedSubAccountIds?.has(subAccountId)) {
        return false;
      }

      return sleeveMatchesAmount(subAccount, kind, cents);
    })
    .sort((a, b) => a.subAccountId.localeCompare(b.subAccountId));

  const match = candidates[0];
  if (!match) {
    return null;
  }

  const holdingLabels = holdingLabelsForSleeve(
    match.subAccount,
    kind,
    cents,
    count,
    diffRows,
  );

  if (holdingLabels.length === 0) {
    return null;
  }

  usedSubAccountIds?.add(match.subAccountId);

  return {
    accountLabel: resolveAccountLabel(match.subAccount, subAccountsById),
    holdingLabels,
    subAccountId: match.subAccountId,
  };
};
